import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ClipboardDocumentIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '@components/PageHeader';
import { EstateMap } from '@components/EstateMap';
import { Button } from '@components/ui/Button';
import { Card } from '@components/ui/Card';
import { Input } from '@components/ui/Input';
import { Autocomplete, AutocompleteOption } from '@components/ui/Autocomplete';
import {
  candidateToEstateJson,
  getGoogleLocationSuggestions,
  getGooglePlaceDetails,
  searchVicProposedEstateCandidates,
} from '@services';
import { useToast } from '@hooks';
import { useEstateStore } from '../../stores/estateStore';
import { jsonDataToItems, jsonDataToMetadata } from '../../utils/mergeEstates';
import type { CandidateStrength, EstateItem, ProposedEstateCandidate } from '@types';
import type { ProposedParcel } from '@types';
import type { GoogleLocationSuggestion } from '@services';

type DraftFields = Pick<
  ProposedEstateCandidate,
  'displayName' | 'suburb' | 'postcode' | 'address' | 'developerName' | 'developerEmail' | 'notes'
>;

interface CandidateSelection {
  includedKeys: string[];
}

interface PlanSummary {
  key: string;
  label: string;
  layerStatus: string;
  total: number;
  included: number;
  saleLots: number;
  references: number;
  approvedContext: number;
}

const strengthStyles: Record<CandidateStrength, string> = {
  strong: 'border-green-200 bg-green-50 text-green-800',
  medium: 'border-amber-200 bg-amber-50 text-amber-800',
  weak: 'border-gray-300 bg-gray-100 text-gray-700',
  ignored: 'border-red-200 bg-red-50 text-red-800',
};

export const ProposedEstates = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { loadEstateWithItems } = useEstateStore();
  const [step, setStep] = useState<'search' | 'results'>('search');
  const [locationSearch, setLocationSearch] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<GoogleLocationSuggestion | null>(null);
  const [locationSuggestions, setLocationSuggestions] = useState<GoogleLocationSuggestion[]>([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [resolvedLocation, setResolvedLocation] = useState<{
    suburb: string;
    postcode: string;
    label: string;
  } | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<ProposedEstateCandidate[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, DraftFields>>({});
  const [isMapReview, setIsMapReview] = useState(false);
  const [selections, setSelections] = useState<Record<string, CandidateSelection>>({});

  const selectedCandidate = useMemo(
    () => candidates.find(c => c.id === selectedId) ?? null,
    [candidates, selectedId]
  );

  const selectedSelection = selectedCandidate ? selections[selectedCandidate.id] : undefined;

  const selectedCandidateWithSelection = useMemo(() => {
    if (!selectedCandidate) return null;
    return applyCandidateSelection(selectedCandidate, selectedSelection);
  }, [selectedCandidate, selectedSelection]);

  const mergedCandidate = useMemo(() => {
    if (!selectedCandidate || !selectedCandidateWithSelection) return null;
    return { ...selectedCandidateWithSelection, ...(drafts[selectedCandidate.id] ?? {}) };
  }, [drafts, selectedCandidate, selectedCandidateWithSelection]);

  const selectedJson = useMemo(
    () => (mergedCandidate ? JSON.stringify(candidateToEstateJson(mergedCandidate), null, 2) : ''),
    [mergedCandidate]
  );

  useEffect(() => {
    if (locationSearch.trim().length < 2) {
      setLocationSuggestions([]);
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      setIsLoadingLocations(true);
      try {
        const suggestions = await getGoogleLocationSuggestions(locationSearch);
        setLocationSuggestions(suggestions);
      } catch (err) {
        console.error('Location suggestions failed:', err);
        setLocationSuggestions([]);
      } finally {
        setIsLoadingLocations(false);
      }
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [locationSearch]);

  const handleLocationSelect = useCallback(
    (option: AutocompleteOption) => {
      const suggestion = locationSuggestions.find(s => s.id === option.id);
      setSelectedLocation(suggestion ?? null);
    },
    [locationSuggestions]
  );

  const handleSearch = async () => {
    setIsSearching(true);
    setError(null);

    try {
      const location = await resolveSearchLocation(locationSearch, selectedLocation);
      setResolvedLocation(location);

      const results = await searchVicProposedEstateCandidates({
        suburb: location.suburb || undefined,
        postcode: location.postcode || undefined,
      });

      setCandidates(results);
      setSelections({});
      setDrafts(
        Object.fromEntries(
          results.map(c => [
            c.id,
            {
              displayName: c.displayName.replace(
                /^Unknown suburb/i,
                location.suburb || 'Unknown suburb'
              ),
              suburb: c.suburb === 'Unknown suburb' ? location.suburb : c.suburb,
              postcode: c.postcode ?? location.postcode,
              address: c.address,
              developerName: c.developerName,
              developerEmail: c.developerEmail,
              notes: c.notes,
            },
          ])
        )
      );
      setSelectedId(results[0]?.id ?? null);

      if (results.length) {
        setStep('results');
        toast(`Found ${results.length} candidate group(s)`, 'success');
      } else {
        toast('No proposed estate candidates found', 'warning');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Search failed';
      setError(message);
      toast(message, 'error');
    } finally {
      setIsSearching(false);
    }
  };

  const handleNewSearch = () => {
    setStep('search');
    setCandidates([]);
    setSelectedId(null);
    setError(null);
    setIsMapReview(false);
    setSelections({});
  };

  const updateDraft = (field: keyof DraftFields, value: string) => {
    if (!selectedCandidate) return;
    setDrafts(prev => ({
      ...prev,
      [selectedCandidate.id]: { ...prev[selectedCandidate.id], [field]: value },
    }));
  };

  const updateSelection = useCallback(
    (updater: (includedKeys: Set<string>) => Set<string>) => {
      if (!selectedCandidate) return;
      const allKeys = allCandidateParcels(selectedCandidate).map(parcelKey);
      const current = new Set(selections[selectedCandidate.id]?.includedKeys ?? allKeys);
      const next = updater(current);
      setSelections(prev => ({
        ...prev,
        [selectedCandidate.id]: { includedKeys: Array.from(next) },
      }));
    },
    [selectedCandidate, selections]
  );

  const includeParcel = useCallback(
    (key: string) => updateSelection(current => new Set(current).add(key)),
    [updateSelection]
  );

  const excludeParcel = useCallback(
    (key: string) =>
      updateSelection(current => {
        const next = new Set(current);
        next.delete(key);
        return next;
      }),
    [updateSelection]
  );

  const includePlan = useCallback(
    (planKey: string) =>
      updateSelection(current => {
        const next = new Set(current);
        allCandidateParcels(selectedCandidate)
          .filter(parcel => planGroupKey(parcel) === planKey)
          .forEach(parcel => next.add(parcelKey(parcel)));
        return next;
      }),
    [selectedCandidate, updateSelection]
  );

  const excludePlan = useCallback(
    (planKey: string) =>
      updateSelection(current => {
        const next = new Set(current);
        allCandidateParcels(selectedCandidate)
          .filter(parcel => planGroupKey(parcel) === planKey)
          .forEach(parcel => next.delete(parcelKey(parcel)));
        return next;
      }),
    [selectedCandidate, updateSelection]
  );

  const setPlanIncluded = useCallback(
    (planKey: string, isIncluded: boolean) =>
      updateSelection(current => {
        const next = new Set(current);
        allCandidateParcels(selectedCandidate)
          .filter(parcel => planGroupKey(parcel) === planKey)
          .forEach(parcel => {
            if (isIncluded) {
              next.add(parcelKey(parcel));
            } else {
              next.delete(parcelKey(parcel));
            }
          });
        return next;
      }),
    [selectedCandidate, updateSelection]
  );

  const onlyPlan = useCallback(
    (planKey: string) =>
      updateSelection(() => {
        const next = new Set<string>();
        allCandidateParcels(selectedCandidate)
          .filter(parcel => planGroupKey(parcel) === planKey)
          .forEach(parcel => next.add(parcelKey(parcel)));
        return next;
      }),
    [selectedCandidate, updateSelection]
  );

  const clearSelection = useCallback(() => {
    if (!selectedCandidate) return;
    setSelections(prev => ({
      ...prev,
      [selectedCandidate.id]: { includedKeys: [] },
    }));
  }, [selectedCandidate]);

  const resetSelection = useCallback(() => {
    if (!selectedCandidate) return;
    setSelections(prev => {
      const next = { ...prev };
      delete next[selectedCandidate.id];
      return next;
    });
  }, [selectedCandidate]);

  const handleCopy = async () => {
    if (!selectedJson) return;
    await navigator.clipboard.writeText(selectedJson);
    toast('JSON copied to clipboard', 'success');
  };

  const handleDownload = () => {
    if (!mergedCandidate || !selectedJson) return;
    const blob = new Blob([selectedJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${mergedCandidate.displayName.replace(/\s+/g, '_') || 'proposed_estate'}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast('JSON downloaded', 'success');
  };

  const handleOpenEditor = () => {
    if (!mergedCandidate) return;
    const estateJson = candidateToEstateJson(mergedCandidate);
    const metadata = jsonDataToMetadata(estateJson);
    loadEstateWithItems(metadata, jsonDataToItems(estateJson));
    navigate(`/edit/${encodeURIComponent(metadata.planNumber || metadata.name || 'proposed-estate')}`, {
      state: { preloaded: true },
    });
  };

  // ── Step 1: Search ──────────────────────────────────────────────────────────
  if (step === 'search') {
    return (
      <div>
        <PageHeader
          title="Proposed Estates"
          subtitle="Discover VIC proposed land candidates without changing existing estate workflows"
        />
        <div className="mx-auto max-w-lg pt-6">
          <Card>
            <h2 className="mb-5 text-xl font-bold text-gray-800">Search VIC Proposed Parcels</h2>
            <div className="space-y-4">
              <Autocomplete
                label="Location"
                placeholder="Search suburb or postcode, e.g., Colac"
                value={locationSearch}
                suggestions={locationSuggestions.map(suggestion => ({
                  id: suggestion.id,
                  label: suggestion.label,
                  value: suggestion.value,
                }))}
                isLoading={isLoadingLocations}
                onChange={value => {
                  setLocationSearch(value);
                  setSelectedLocation(null);
                  setResolvedLocation(null);
                }}
                onSelect={handleLocationSelect}
                forceUppercase={false}
              />

              {selectedLocation && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <p className="text-xs font-bold uppercase text-gray-500">
                    Selected location
                  </p>
                  <p className="mt-1 text-sm text-gray-700">{selectedLocation.label}</p>
                </div>
              )}

              {error && (
                <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>
              )}

              <Button isLoading={isSearching} onClick={handleSearch} className="w-full">
                <MagnifyingGlassIcon className="h-5 w-5" />
                Search Candidates
              </Button>
            </div>
            <p className="mt-4 text-xs text-gray-400">
              Proposed parcel data is a discovery signal only. Human review required before anything
              is considered marketable.
            </p>
          </Card>
        </div>
      </div>
    );
  }

  // ── Step 2: Results — full-height split shell ───────────────────────────────
  //
  // -m-4 / -m-8 escapes the main padding so the layout sits edge-to-edge.
  // Height subtracts only the fixed TopBar offset (mt-16 / mt-20) so the
  // panel fills exactly to the bottom of the viewport.
  return (
    <div className="-m-4 flex h-[calc(100vh-4rem)] flex-col md:-m-8 md:h-[calc(100vh-5rem)]">
      {/* Slim context bar */}
      <div className="flex shrink-0 items-center justify-between border-b border-gray-200 bg-white px-6 py-3">
        <div className="flex items-center gap-3">
          <button
            className="flex items-center gap-1.5 text-sm text-gray-500 transition-colors hover:text-gray-900"
            onClick={handleNewSearch}
          >
            <ArrowLeftIcon className="h-4 w-4" />
            New search
          </button>
          <span className="text-gray-300">|</span>
          <span className="text-sm text-gray-600">
            {candidates.length} candidate{candidates.length !== 1 ? 's' : ''}
            {resolvedLocation?.label ? ` - ${resolvedLocation.label}` : ''}
          </span>
        </div>
        <Button variant="outlined" isLoading={isSearching} onClick={handleSearch}>
          <MagnifyingGlassIcon className="h-4 w-4" />
          Refine
        </Button>
      </div>

      {/* Split layout — fills remaining height */}
      <div className="flex min-h-0 flex-1">
        {/* Left: scrollable candidate list */}
        <div className="flex w-72 shrink-0 flex-col border-r border-gray-200 bg-white">
          <div className="border-b border-gray-100 px-4 py-3">
            <p className="text-xs font-bold uppercase text-gray-400">Candidates</p>
          </div>
          <div className="flex-1 space-y-1 overflow-y-auto p-3">
            {candidates.map(candidate => (
              <button
                key={candidate.id}
                className={`w-full rounded-lg border p-3 text-left transition-colors ${
                  selectedId === candidate.id
                    ? 'border-gold bg-gold-light'
                    : 'border-transparent hover:border-gray-200 hover:bg-gray-50'
                }`}
                onClick={() => setSelectedId(candidate.id)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-gray-800">
                      {candidate.displayName}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500">
                      {candidate.parcels.length} sale lots - {candidate.suburb}
                    </p>
                  </div>
                  <StrengthBadge candidate={candidate} />
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Right: pinned plan preview + scrollable form below */}
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-gray-50">
          {mergedCandidate ? (
            <>
              {/* Pinned — plan preview never leaves view */}
              <div className="shrink-0 border-b border-gray-200 bg-white p-6">
                <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">
                      {mergedCandidate.displayName}
                    </h2>
                    <p className="text-sm text-gray-500">
                      {mergedCandidate.parcels.length} sale lots - score{' '}
                      {mergedCandidate.score.score}/100
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <StrengthBadge candidate={mergedCandidate} />
                    <Button variant="outlined" onClick={() => setIsMapReview(prev => !prev)}>
                      {isMapReview ? 'Show preview' : 'Show on map'}
                    </Button>
                    <Button variant="outlined" onClick={handleOpenEditor}>
                      Edit selected
                    </Button>
                    <Button variant="outlined" onClick={handleCopy}>
                      <ClipboardDocumentIcon className="h-4 w-4" />
                      Copy JSON
                    </Button>
                    <Button onClick={handleDownload}>
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      Download
                    </Button>
                  </div>
                </div>
                <CandidatePreview
                  baseCandidate={selectedCandidate ?? mergedCandidate}
                  candidate={mergedCandidate}
                  includeParcel={includeParcel}
                  excludeParcel={excludeParcel}
                  includePlan={includePlan}
                  onlyPlan={onlyPlan}
                  excludePlan={excludePlan}
                  setPlanIncluded={setPlanIncluded}
                  isMapReview={isMapReview}
                  clearSelection={clearSelection}
                  resetSelection={resetSelection}
                  selection={selectedSelection}
                />
              </div>

              {/* Scrollable — edit fields + export */}
              <div className="flex-1 space-y-4 overflow-y-auto p-6">
                <Card>
                  <p className="mb-4 text-xs font-bold uppercase text-gray-500">Edit Details</p>
                  <div className="grid gap-4 lg:grid-cols-2">
                    <Input
                      label="Display name"
                      value={mergedCandidate.displayName}
                      onChange={e => updateDraft('displayName', e.target.value)}
                    />
                    <Input
                      label="Suburb"
                      value={mergedCandidate.suburb}
                      onChange={e => updateDraft('suburb', e.target.value)}
                    />
                    <Input
                      label="Postcode"
                      value={mergedCandidate.postcode ?? ''}
                      onChange={e => updateDraft('postcode', e.target.value)}
                    />
                    <Input
                      label="Address"
                      value={mergedCandidate.address ?? ''}
                      onChange={e => updateDraft('address', e.target.value)}
                    />
                    <Input
                      label="Developer name"
                      value={mergedCandidate.developerName ?? ''}
                      onChange={e => updateDraft('developerName', e.target.value)}
                    />
                    <Input
                      label="Developer email"
                      value={mergedCandidate.developerEmail ?? ''}
                      onChange={e => updateDraft('developerEmail', e.target.value)}
                    />
                  </div>
                  <div className="mt-4">
                    <label className="mb-2 block text-xs font-bold uppercase text-gray-500">
                      Review notes
                    </label>
                    <textarea
                      className="input-field min-h-24"
                      value={mergedCandidate.notes ?? ''}
                      onChange={e => updateDraft('notes', e.target.value)}
                    />
                  </div>
                </Card>

                <Card>
                  <p className="mb-1 text-xs font-bold uppercase text-gray-500">Export JSON</p>
                  <p className="mb-3 text-sm text-gray-400">
                    Draft BlocPlus-compatible shape for manual review only.
                  </p>
                  <pre className="max-h-96 overflow-auto rounded-lg bg-gray-100 p-4 text-xs">
                    {selectedJson}
                  </pre>
                </Card>
              </div>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center">
              <p className="text-sm text-gray-400">Select a candidate from the list to review</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StrengthBadge = ({ candidate }: { candidate: ProposedEstateCandidate }) => (
  <span
    className={`shrink-0 rounded-full border px-3 py-1 text-xs font-bold uppercase ${
      strengthStyles[candidate.score.strength]
    }`}
  >
    {candidate.score.strength}
  </span>
);

const CandidatePreview = ({
  baseCandidate,
  candidate,
  includeParcel,
  excludeParcel,
  includePlan,
  onlyPlan,
  excludePlan,
  setPlanIncluded,
  isMapReview,
  clearSelection,
  resetSelection,
  selection,
}: {
  baseCandidate: ProposedEstateCandidate;
  candidate: ProposedEstateCandidate;
  includeParcel: (key: string) => void;
  excludeParcel: (key: string) => void;
  includePlan: (planKey: string) => void;
  onlyPlan: (planKey: string) => void;
  excludePlan: (planKey: string) => void;
  setPlanIncluded: (planKey: string, isIncluded: boolean) => void;
  isMapReview: boolean;
  clearSelection: () => void;
  resetSelection: () => void;
  selection?: CandidateSelection;
}) => {
  if (isMapReview) {
    return (
      <CandidateMapReview
        baseCandidate={baseCandidate}
        candidate={candidate}
        includeParcel={includeParcel}
        excludeParcel={excludeParcel}
        includePlan={includePlan}
        onlyPlan={onlyPlan}
        excludePlan={excludePlan}
        setPlanIncluded={setPlanIncluded}
        clearSelection={clearSelection}
        resetSelection={resetSelection}
        selection={selection}
      />
    );
  }

  const approvedPolygons = (candidate.approvedContextParcels ?? [])
    .slice(0, 120)
    .map(parcel => projectPoints(parcel.points, candidate));
  const ignoredPolygons = (candidate.ignoredParcels ?? [])
    .slice(0, 80)
    .map(parcel => projectPoints(parcel.points, candidate));
  const salePolygons = candidate.parcels
    .slice(0, 80)
    .map(parcel => projectPoints(parcel.points, candidate));

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-100 p-3">
      <svg
        aria-label="Candidate geometry preview"
        className="h-64 w-full rounded-md bg-white"
        role="img"
        viewBox="0 0 600 280"
      >
        <rect fill="#f8f8f8" height="280" width="600" />
        {approvedPolygons.map((points, index) => (
          <polygon
            key={`approved-${index}`}
            fill="none"
            points={points}
            stroke="#a8adb3"
            strokeWidth="1"
          />
        ))}
        {ignoredPolygons.map((points, index) => (
          <polygon
            key={`ignored-${index}`}
            fill="#e2e5e8"
            fillOpacity="0.3"
            points={points}
            stroke="#8b9199"
            strokeDasharray="5 4"
            strokeWidth="1.2"
          />
        ))}
        {salePolygons.map((points, index) => (
          <polygon
            key={`sale-${index}`}
            fill="#c7c9cc"
            fillOpacity="0.76"
            points={points}
            stroke="#3f444a"
            strokeWidth="1.7"
          />
        ))}
      </svg>
      <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-600">
        <span>
          <span className="mr-1 inline-block h-3 w-3 border border-gray-500 bg-gray-300 align-middle" />
          {candidate.parcels.length} proposed sale lot(s)
        </span>
        <span>
          <span className="mr-1 inline-block h-3 w-3 border border-gray-400 bg-white align-middle" />
          {(candidate.approvedContextParcels ?? []).length} approved/titled context lot(s)
        </span>
        <span>
          <span className="mr-1 inline-block h-3 w-3 border border-dashed border-gray-500 bg-gray-200 align-middle" />
          {(candidate.ignoredParcels ?? []).length} ignored proposed context parcel(s)
        </span>
      </div>
      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        <div>
          <p className="text-xs font-bold uppercase text-gray-700">Scoring reasons</p>
          <ul className="mt-2 space-y-1 text-sm text-gray-600">
            {candidate.score.reasons.map(reason => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-xs font-bold uppercase text-gray-700">Source</p>
          <p className="mt-2 text-sm text-gray-600">{candidate.source.dataset}</p>
          <p className="mt-1 text-xs text-gray-500">
            Imported {new Date(candidate.source.importedAt).toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
};

const CandidateMapReview = ({
  baseCandidate,
  candidate,
  includeParcel,
  excludeParcel,
  includePlan,
  onlyPlan,
  excludePlan,
  setPlanIncluded,
  clearSelection,
  resetSelection,
  selection,
}: {
  baseCandidate: ProposedEstateCandidate;
  candidate: ProposedEstateCandidate;
  includeParcel: (key: string) => void;
  excludeParcel: (key: string) => void;
  includePlan: (planKey: string) => void;
  onlyPlan: (planKey: string) => void;
  excludePlan: (planKey: string) => void;
  setPlanIncluded: (planKey: string, isIncluded: boolean) => void;
  clearSelection: () => void;
  resetSelection: () => void;
  selection?: CandidateSelection;
}) => {
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [selectedPlanKey, setSelectedPlanKey] = useState<string | null>(null);
  const [planSearch, setPlanSearch] = useState('');
  const includedKeys = useMemo(() => {
    const allKeys = allCandidateParcels(baseCandidate).map(parcelKey);
    return new Set(selection?.includedKeys ?? allKeys);
  }, [baseCandidate, selection]);
  const items = useMemo(
    () => candidateToMapItems(baseCandidate, includedKeys),
    [baseCandidate, includedKeys]
  );
  const planSummaries = useMemo(
    () => summarizePlans(baseCandidate, includedKeys),
    [baseCandidate, includedKeys]
  );
  const selectedParcel = useMemo(
    () => allCandidateParcels(baseCandidate).find(parcel => parcelKey(parcel) === selectedCode),
    [baseCandidate, selectedCode]
  );
  const selectedPlan = useMemo(
    () => planSummaries.find(plan => plan.key === selectedPlanKey) ?? null,
    [planSummaries, selectedPlanKey]
  );
  const highlightedCodes = useMemo(
    () =>
      selectedPlanKey
        ? allCandidateParcels(baseCandidate)
            .filter(parcel => planGroupKey(parcel) === selectedPlanKey)
            .map(parcelKey)
        : [],
    [baseCandidate, selectedPlanKey]
  );
  const filteredPlanSummaries = useMemo(() => {
    const term = planSearch.trim().toLowerCase();
    if (!term) return planSummaries;
    return planSummaries.filter(plan =>
      [plan.label, plan.layerStatus].some(value => value.toLowerCase().includes(term))
    );
  }, [planSearch, planSummaries]);
  const handleMapParcelClick = useCallback(
    (code: string) => {
      setSelectedCode(code);
      const parcel = allCandidateParcels(baseCandidate).find(item => parcelKey(item) === code);
      setSelectedPlanKey(parcel ? planGroupKey(parcel) : null);
    },
    [baseCandidate]
  );
  const handleClearSelection = useCallback(() => {
    setSelectedCode(null);
    setSelectedPlanKey(null);
    clearSelection();
  }, [clearSelection]);
  const handleResetSelection = useCallback(() => {
    setSelectedCode(null);
    setSelectedPlanKey(null);
    resetSelection();
  }, [resetSelection]);

  return (
    <div className="grid gap-4 rounded-lg border border-gray-200 bg-white p-3 xl:grid-cols-[minmax(0,1fr)_360px]">
      <div>
        <EstateMap
          center={candidate.center}
          className="h-[420px] rounded-md"
          items={items}
          selectedCode={selectedCode}
          highlightedCodes={highlightedCodes}
          onItemClick={handleMapParcelClick}
          showTooltips={false}
        />
        <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-600">
          <span>{candidate.parcels.length} selected proposed sale lot(s)</span>
          <span>
            {(candidate.ignoredParcels ?? []).filter(parcel => parcel.referenceType).length}{' '}
            selected proposed reference area(s)
          </span>
          <span>{candidate.approvedContextParcels?.length ?? 0} selected approved/titled context lot(s)</span>
          <span>{items.filter(item => item.classification === 'excluded').length} excluded map item(s)</span>
        </div>
      </div>

      <div className="flex min-h-0 flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase text-gray-500">Candidate Builder</p>
            <p className="text-xs text-gray-400">Click a parcel, then add or remove its whole POS.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outlined" onClick={handleClearSelection}>
              Clear all
            </Button>
            <Button variant="outlined" onClick={handleResetSelection}>
              Reset
            </Button>
          </div>
        </div>

        <div className="rounded-lg border border-gold/40 bg-gold-light/30 p-3">
          <p className="text-xs font-bold uppercase text-gray-500">Selected POS</p>
          {selectedPlan ? (
            <div className="mt-2 space-y-3">
              <div>
                <p className="text-base font-bold text-gray-900">{selectedPlan.label}</p>
                <p className="text-xs text-gray-600">
                  {selectedPlan.included}/{selectedPlan.total} selected - {selectedPlan.layerStatus} -{' '}
                  {selectedPlan.saleLots} lots - {selectedPlan.references} refs -{' '}
                  {selectedPlan.approvedContext} approved
                </p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Button variant="outlined" onClick={() => includePlan(selectedPlan.key)}>
                  Add POS
                </Button>
                <Button variant="outlined" onClick={() => excludePlan(selectedPlan.key)}>
                  Remove POS
                </Button>
                <Button variant="outlined" onClick={() => onlyPlan(selectedPlan.key)}>
                  Only POS
                </Button>
              </div>
            </div>
          ) : (
            <p className="mt-2 text-sm text-gray-500">Click any parcel on the map to identify its POS.</p>
          )}
        </div>

        <Input
          label="Find POS"
          onChange={event => setPlanSearch(event.target.value)}
          placeholder="Search e.g. PS918223"
          value={planSearch}
        />

        <div
          className="max-h-80 overflow-y-auto rounded-lg border border-gray-200"
          onWheel={event => event.stopPropagation()}
        >
          {filteredPlanSummaries.map(plan => (
            <div
              key={plan.key}
              className={`flex items-center gap-3 border-b border-gray-100 p-3 last:border-b-0 ${
                plan.included > 0 ? 'bg-gold-light/40' : 'bg-white'
              } ${plan.key === selectedPlanKey ? 'ring-2 ring-gold' : ''}`}
            >
              <input
                id={`plan-${plan.key}`}
                checked={plan.included > 0}
                className="h-4 w-4 shrink-0 accent-[#b0894f]"
                onChange={event => setPlanIncluded(plan.key, event.target.checked)}
                type="checkbox"
              />
              <label
                className="min-w-0 cursor-pointer"
                htmlFor={`plan-${plan.key}`}
                onClick={() => setSelectedPlanKey(plan.key)}
              >
                <p className="truncate text-sm font-bold text-gray-800">{plan.label}</p>
                <p className="text-xs text-gray-500">
                  {plan.included}/{plan.total} selected - {plan.layerStatus}
                </p>
                <p className="text-xs text-gray-400">
                  {plan.saleLots} lots - {plan.references} refs - {plan.approvedContext} approved
                </p>
              </label>
              <div className="ml-auto flex shrink-0 gap-1">
                <button
                  className="rounded border border-gold px-2 py-1 text-xs font-bold text-gold-dark hover:bg-gold-light"
                  onClick={() => {
                    setSelectedPlanKey(plan.key);
                    onlyPlan(plan.key);
                  }}
                  type="button"
                >
                  Only
                </button>
              </div>
            </div>
          ))}
          {!filteredPlanSummaries.length && (
            <p className="p-3 text-sm text-gray-400">No POS groups match that search.</p>
          )}
        </div>

        <div className="rounded-lg border border-gray-200 p-3">
          <p className="mb-2 text-xs font-bold uppercase text-gray-500">Clicked Parcel</p>
          {selectedParcel ? (
            <div className="space-y-2">
              <div>
                <p className="text-sm font-bold text-gray-800">
                  {selectedParcel.lotNumber || 'Unnamed parcel'}
                </p>
                <p className="text-xs text-gray-500">
                  {selectedParcel.planNumber || 'No POS'} - {selectedParcel.layerStatus || 'unknown'}
                </p>
                <p className="text-xs text-gray-400">
                  {parcelRoleLabel(selectedParcel)} -{' '}
                  {includedKeys.has(parcelKey(selectedParcel)) ? 'included' : 'excluded'}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {includedKeys.has(parcelKey(selectedParcel)) ? (
                  <Button variant="outlined" onClick={() => excludeParcel(parcelKey(selectedParcel))}>
                    Remove parcel
                  </Button>
                ) : (
                  <Button variant="outlined" onClick={() => includeParcel(parcelKey(selectedParcel))}>
                    Add parcel
                  </Button>
                )}
                {planSummaries.find(plan => plan.key === planGroupKey(selectedParcel))?.included ? (
                  <Button variant="outlined" onClick={() => excludePlan(planGroupKey(selectedParcel))}>
                    Untick POS
                  </Button>
                ) : (
                  <Button variant="outlined" onClick={() => includePlan(planGroupKey(selectedParcel))}>
                    Tick POS
                  </Button>
                )}
                <Button variant="outlined" onClick={() => onlyPlan(planGroupKey(selectedParcel))}>
                  Only POS
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-400">Click a parcel on the map to inspect it.</p>
          )}
        </div>
      </div>
    </div>
  );
};

function candidateToMapItems(candidate: ProposedEstateCandidate, includedKeys: Set<string>): EstateItem[] {
  const lotItems: EstateItem[] = candidate.parcels.map((parcel, index) => ({
    code: parcelKey(parcel),
    handleid: parcel.sourceId || `proposed-lot-${index}`,
    landsize: Math.round(parcel.areaSqm || estimateArea(parcel.points)),
    frontage: 0,
    price: 0,
    status: 'available',
    points: parcel.points,
    classification: includedKeys.has(parcelKey(parcel)) ? 'lot' : 'excluded',
  }));

  const referenceItems: EstateItem[] = (candidate.ignoredParcels ?? [])
    .filter(parcel => parcel.referenceType)
    .map((parcel, index) => ({
      code: parcelKey(parcel),
      handleid: parcel.sourceId || `proposed-reference-${index}`,
      landsize: Math.round(parcel.areaSqm || estimateArea(parcel.points)),
      frontage: 0,
      price: 0,
      status: '',
      points: parcel.points,
      classification: includedKeys.has(parcelKey(parcel))
        ? parcel.referenceType === 'future'
          ? 'proposed-context'
          : parcel.referenceType ?? 'proposed-context'
        : 'excluded',
    }));

  const approvedContextItems: EstateItem[] = (candidate.approvedContextParcels ?? [])
    .map((parcel, index) => ({
      code: parcelKey(parcel),
      handleid: parcel.sourceId || `approved-context-${index}`,
      landsize: Math.round(parcel.areaSqm || estimateArea(parcel.points)),
      frontage: 0,
      price: 0,
      status: '',
      points: parcel.points,
      classification: includedKeys.has(parcelKey(parcel)) ? 'approved-context' : 'excluded',
    }));

  return [...approvedContextItems, ...referenceItems, ...lotItems];
}

function applyCandidateSelection(
  candidate: ProposedEstateCandidate,
  selection?: CandidateSelection
): ProposedEstateCandidate {
  if (!selection) return candidate;

  const included = new Set(selection.includedKeys);
  const filtered = {
    ...candidate,
    parcels: candidate.parcels.filter(parcel => included.has(parcelKey(parcel))),
    approvedContextParcels: (candidate.approvedContextParcels ?? []).filter(parcel =>
      included.has(parcelKey(parcel))
    ),
    ignoredParcels: (candidate.ignoredParcels ?? []).filter(parcel => included.has(parcelKey(parcel))),
  };
  const points = allCandidateParcels(filtered).flatMap(parcel => parcel.points);
  if (!points.length) return filtered;

  const bounds = boundsFromPoints(points);
  return {
    ...filtered,
    bounds,
    center: {
      lat: (bounds.north + bounds.south) / 2,
      lng: (bounds.east + bounds.west) / 2,
    },
  };
}

function allCandidateParcels(candidate: ProposedEstateCandidate | null): ProposedParcel[] {
  if (!candidate) return [];
  return [
    ...candidate.parcels,
    ...(candidate.ignoredParcels ?? []),
    ...(candidate.approvedContextParcels ?? []),
  ];
}

function parcelKey(parcel: ProposedParcel): string {
  return `${parcel.layerStatus ?? 'unknown'}:${parcel.sourceId || parcel.id}`;
}

function planGroupKey(parcel: ProposedParcel): string {
  return `${parcel.layerStatus ?? 'unknown'}:${parcel.planNumber || 'NO_PLAN'}`;
}

function summarizePlans(
  candidate: ProposedEstateCandidate,
  includedKeys: Set<string>
): PlanSummary[] {
  const summaries = new Map<string, PlanSummary>();

  for (const parcel of allCandidateParcels(candidate)) {
    const key = planGroupKey(parcel);
    const label = parcel.planNumber || 'No POS';
    const summary =
      summaries.get(key) ??
      {
        key,
        label,
        layerStatus: parcel.layerStatus || 'unknown',
        total: 0,
        included: 0,
        saleLots: 0,
        references: 0,
        approvedContext: 0,
      };

    summary.total += 1;
    if (includedKeys.has(parcelKey(parcel))) summary.included += 1;
    if (parcel.reviewRole === 'sale-lot') summary.saleLots += 1;
    if (parcel.layerStatus === 'approved') summary.approvedContext += 1;
    if (parcel.referenceType) summary.references += 1;
    summaries.set(key, summary);
  }

  return Array.from(summaries.values()).sort(
    (a, b) =>
      layerSortOrder(a.layerStatus) - layerSortOrder(b.layerStatus) ||
      a.label.localeCompare(b.label, undefined, { numeric: true })
  );
}

function layerSortOrder(layerStatus: string): number {
  if (layerStatus === 'proposed') return 0;
  if (layerStatus === 'approved') return 1;
  return 2;
}

function parcelRoleLabel(parcel: ProposedParcel): string {
  if (parcel.reviewRole === 'sale-lot') return 'proposed sale lot';
  if (parcel.layerStatus === 'approved') return 'approved/titled context';
  if (parcel.referenceType) return `${parcel.referenceType} reference`;
  return parcel.ignoreReason || 'context';
}

function boundsFromPoints(points: { lat: number; lng: number }[]) {
  return points.reduce(
    (bounds, point) => ({
      north: Math.max(bounds.north, point.lat),
      south: Math.min(bounds.south, point.lat),
      east: Math.max(bounds.east, point.lng),
      west: Math.min(bounds.west, point.lng),
    }),
    { north: -Infinity, south: Infinity, east: -Infinity, west: Infinity }
  );
}

function estimateArea(points: { lat: number; lng: number }[]): number {
  if (points.length < 3) return 0;
  const lat = points.reduce((sum, point) => sum + point.lat, 0) / points.length;
  const metersPerLng = 111_320 * Math.cos((lat * Math.PI) / 180);
  const metersPerLat = 110_574;
  const projected = points.map(point => ({
    x: point.lng * metersPerLng,
    y: point.lat * metersPerLat,
  }));

  let area = 0;
  for (let i = 0; i < projected.length; i++) {
    const current = projected[i];
    const next = projected[(i + 1) % projected.length];
    area += current.x * next.y - next.x * current.y;
  }
  return Math.abs(area / 2);
}

function projectPoints(
  points: { lat: number; lng: number }[],
  candidate: ProposedEstateCandidate
): string {
  const { bounds } = candidate;
  const width = Math.max(bounds.east - bounds.west, 0.000001);
  const height = Math.max(bounds.north - bounds.south, 0.000001);

  return points
    .map(point => {
      const x = 24 + ((point.lng - bounds.west) / width) * 552;
      const y = 256 - ((point.lat - bounds.south) / height) * 232;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
}

async function resolveSearchLocation(
  query: string,
  selectedLocation: GoogleLocationSuggestion | null
): Promise<{ suburb: string; postcode: string; label: string }> {
  const trimmed = query.trim();
  if (!trimmed) {
    throw new Error('Search for a VIC suburb or postcode before searching candidates.');
  }

  if (selectedLocation) {
    const details = await getGooglePlaceDetails(selectedLocation.placeId);
    if (!details) {
      throw new Error('Could not resolve selected location. Try another suggestion.');
    }

    if (details.state && details.state !== 'VIC') {
      throw new Error('Proposed estate discovery is currently VIC only.');
    }

    const suburb = details.locality || selectedLocation.mainText.replace(/\s+VIC$/i, '');
    const postcode = details.postcode || extractPostcode(selectedLocation.description);

    return {
      suburb: suburb.trim(),
      postcode,
      label: [suburb, 'VIC', postcode].filter(Boolean).join(' '),
    };
  }

  const postcode = extractPostcode(trimmed);
  return {
    suburb: postcode ? '' : trimmed.replace(/,\s*VIC.*$/i, '').trim(),
    postcode,
    label: trimmed,
  };
}

function extractPostcode(value: string): string {
  return value.match(/\b3\d{3}\b/)?.[0] ?? '';
}
